import {AssignmentsURLParams, RequiredActions} from '@features/home/types.ts'
import {capitalize, formatCurrency, ObjectEntries} from '@utilities/helpers.ts'

import {RequiredActionCard, RequiredActionCardProps} from '@features/home/components/required-action-card.tsx'
import {
    AlertCircleIcon,
    AlignLeftIcon,
    CalendarIcon,
    // Camera01Icon,
    PlusSquareIcon,
    Upload04Icon
} from '@components/ui/icon'
import {RequiredActionsTitle} from '@features/home/components/home-atoms'
import {Assignment} from '@features/assignment/types.ts'
import {StatusDot} from '@features/home/components/status-dot'
import {TableBodyCellText} from '@components/ui/table-atoms'
import {ButtonLink} from '@components/commons/button-link'
import {generatePath} from 'react-router-dom'
import {routes} from '@utilities/constants/routes.tsx'
import i18n, {TKey} from '@/translations/i18n'

export const ASSIGNMENTS_FILTERS = [
    {
        id: 'coordination',
        name: 'home:coordination'
    },
    {
        id: 'pending_submission',
        name: 'home:pending_submission'
    },
    {
        id: 'needs_more_assets',
        name: 'home:needs_more_assets'
    },
    {
        id: 'upcoming',
        name: 'home:upcoming'
    },
    {
        id: 'completed',
        name: 'dashboard:calendar:filters:completed'
    }
] as const satisfies Array<{id: AssignmentsURLParams['filter_value']; name: TKey}>

export const TABLE_ELEMENT_LIMIT = 8

export const getServiceName = (entityTypeName: string, categoryName: string, mediaName: string) => {
    // ${capitalize(shoot_service?.name)} temporary removed. It was placed after entity_type
    return `
    ${capitalize(entityTypeName)}
    ${capitalize(
        categoryName == 'Japan' ? 'Private Room' : categoryName == 'ERF-H1-2024' ? 'Production' : categoryName
    )}
    ${capitalize(mediaName == 'inspection' ? 'Walkthrough' : mediaName)}
  `
}

export interface RenderRequiredActionsProps {
    data: RequiredActions
    cardBorder?: RequiredActionCardProps['variant']
    showInsightsCard?: boolean
}
export const renderRequiredActions = ({data, cardBorder}: RenderRequiredActionsProps) =>
    ObjectEntries(data)
        .map(([key, value]) => {
            switch (key) {
                case 'new_assignments':
                    return value.length > 0
                        ? value.map((assignment, index) => (
                              <RequiredActionCard
                                  variant={cardBorder}
                                  key={`${key}-${index}`}
                                  title={
                                      <RequiredActionsTitle variant={'blue'}>
                                          {i18n.t('home:required_actions:new_assignment')}
                                      </RequiredActionsTitle>
                                  }
                                  icon={<PlusSquareIcon className="text-blue-500" />}
                                  description={
                                      assignment.total_amount && assignment.entity.city
                                          ? i18n.t('home:required_actions:amount_in_city', {
                                                amount: formatCurrency(assignment.total_amount, 'USD'),
                                                city: assignment.entity.city
                                            })
                                          : ''
                                  }
                                  cta={
                                      <ButtonLink
                                          to={generatePath(routes.ASSIGNMENT.handle.fullPath, {
                                              code: assignment.code.toString()
                                          })}
                                          variant={'tertiary'}
                                          shape={'text'}
                                      >
                                          {i18n.t('assignment:make_a_decision')}
                                      </ButtonLink>
                                  }
                              />
                          ))
                        : null
                case 'is_calendar_disconnected':
                    return value ? (
                        <RequiredActionCard
                            variant={cardBorder}
                            key={key}
                            title={
                                <RequiredActionsTitle>
                                    {i18n.t('home:required_actions:calendar_disconnected')}
                                </RequiredActionsTitle>
                            }
                            description={i18n.t('home:required_actions:keep_track_assignments')}
                            icon={<CalendarIcon />}
                            cta={
                                <ButtonLink
                                    to={`${routes.SETTINGS.handle.fullPath}#calendar-section`}
                                    variant={'tertiary'}
                                    shape={'text'}
                                >
                                    {i18n.t('home:required_actions:connect')}
                                </ButtonLink>
                            }
                        />
                    ) : null
                case 'host_shared_dates':
                    return value.length > 0
                        ? value.map((assignment, index) => (
                              <RequiredActionCard
                                  variant={cardBorder}
                                  key={`${key}-${index}`}
                                  title={
                                      <RequiredActionsTitle>
                                          {i18n.t('home:required_actions:host_proposed_dates')}
                                      </RequiredActionsTitle>
                                  }
                                  description={assignment.entity.title || assignment.entity.external_id}
                                  icon={<CalendarIcon />}
                                  cta={
                                      <ButtonLink
                                          to={generatePath(routes.ASSIGNMENT.handle.fullPath, {
                                              code: assignment.code.toString()
                                          })}
                                          variant={'tertiary'}
                                          shape={'text'}
                                      >
                                          {i18n.t('home:required_actions:review')}
                                      </ButtonLink>
                                  }
                              />
                          ))
                        : null
                case 'deliverable_issue':
                    return value.length > 0
                        ? value.map((assignment, index) => (
                              <RequiredActionCard
                                  variant={cardBorder}
                                  key={`${key}-${index}`}
                                  title={
                                      <RequiredActionsTitle>
                                          {i18n.t('home:required_actions:deliverables_issue')}
                                      </RequiredActionsTitle>
                                  }
                                  description={assignment.entity.title || assignment.entity.external_id}
                                  icon={<AlertCircleIcon className="text-danger-600" />}
                                  cta={
                                      <ButtonLink
                                          to={generatePath(routes.ASSIGNMENT.handle.fullPath, {
                                              code: assignment.code.toString()
                                          })}
                                          variant={'tertiary'}
                                          shape={'text'}
                                      >
                                          {i18n.t('home:required_actions:upload_assets')}
                                      </ButtonLink>
                                  }
                              />
                          ))
                        : null
                case 'late_delivery':
                    return value.length > 0
                        ? value.map((assignment, index) => (
                              <RequiredActionCard
                                  variant={cardBorder}
                                  key={`${key}-${index}`}
                                  title={
                                      <RequiredActionsTitle>
                                          {i18n.t('home:required_actions:late_delivery')}
                                      </RequiredActionsTitle>
                                  }
                                  description={assignment.entity.title || assignment.entity.external_id}
                                  icon={<Upload04Icon className="text-danger-600" />}
                                  cta={
                                      <ButtonLink
                                          to={generatePath(routes.ASSIGNMENT.handle.fullPath, {
                                              code: assignment.code.toString()
                                          })}
                                          variant={'tertiary'}
                                          shape={'text'}
                                      >
                                          {i18n.t('home:required_actions:upload_assets')}
                                      </ButtonLink>
                                  }
                              />
                          ))
                        : null
                case 'coordination_overdue':
                    return value.length > 0
                        ? value.map((assignment, index) => (
                              <RequiredActionCard
                                  variant={cardBorder}
                                  key={`${key}-${index}`}
                                  title={
                                      <RequiredActionsTitle>
                                          {i18n.t('home:required_actions:coordination_overdue')}
                                      </RequiredActionsTitle>
                                  }
                                  description={assignment.entity.title || assignment.entity.external_id}
                                  icon={<AlertCircleIcon className="text-warning-600" />}
                                  cta={
                                      <ButtonLink
                                          to={generatePath(routes.ASSIGNMENT.handle.fullPath, {
                                              code: assignment.code.toString()
                                          })}
                                          variant={'tertiary'}
                                          shape={'text'}
                                      >
                                          {i18n.t('home:required_actions:update')}
                                      </ButtonLink>
                                  }
                              />
                          ))
                        : null

                case 'has_new_insights':
                    // TODO: uncomment this card when we have the feature @InsightsPage and approved by legal
                    return null
                // return value && showInsightsCard ? (
                //     <RequiredActionCard
                //         variant={cardBorder}
                //         key={key}
                //         title={
                //             <RequiredActionsTitle>
                //                 {i18n.t('home:required_actions:new_insights')}
                //             </RequiredActionsTitle>
                //         }
                //         icon={<Camera01Icon className='text-blue-500' />}
                //         cta={
                //             <ButtonLink to={routes.INSIGHTS.handle.fullPath} variant={'tertiary'} shape={'text'}>
                //                 {i18n.t('home:required_actions:review_feedback')}
                //             </ButtonLink>
                //         }
                //     />
                // ) : null
                case 'is_introducing_missing':
                    return value ? (
                        <RequiredActionCard
                            key={key}
                            variant={cardBorder}
                            title={
                                <RequiredActionsTitle>
                                    {i18n.t('home:required_actions:introduction_missing')}
                                </RequiredActionsTitle>
                            }
                            description={i18n.t('home:required_actions:hosts_better')}
                            icon={<AlignLeftIcon />}
                            cta={
                                <ButtonLink to={routes.SETTINGS.handle.fullPath} variant={'tertiary'} shape={'text'}>
                                    {i18n.t('home:required_actions:add_intro')}
                                </ButtonLink>
                            }
                        />
                    ) : null
                case 'is_calendar_connected':
                    return !value ? (
                        <RequiredActionCard
                            variant={cardBorder}
                            key={key}
                            title={
                                <RequiredActionsTitle>
                                    {i18n.t('home:required_actions:no_calendar_connected')}
                                </RequiredActionsTitle>
                            }
                            description={i18n.t('home:required_actions:keep_track_assignments')}
                            icon={<CalendarIcon />}
                            cta={
                                <ButtonLink
                                    preventScrollReset
                                    to={`${routes.SETTINGS.handle.fullPath}#calendar-section`}
                                    variant={'tertiary'}
                                    shape={'text'}
                                >
                                    {i18n.t('home:required_actions:connect')}
                                </ButtonLink>
                            }
                        />
                    ) : null
                default:
                    return null
            }
        })
        .flat()
        .filter(Boolean) || []

export const renderStatusFromAssignment = (assignment: Assignment) => {
    if (assignment.status == 'accepted') {
        switch (assignment.task_status.status) {
            case 'assets_in_review':
                return (
                    <div className="flex items-center gap-2">
                        <StatusDot variant={'blue'} />{' '}
                        <TableBodyCellText variant={'dark'}>
                            {i18n.t('home:table:statuses:assets_in_review')}
                        </TableBodyCellText>
                    </div>
                )
            case 'hard_cancelled':
                return (
                    <div className="flex items-center gap-2">
                        <StatusDot variant={'purple'} />{' '}
                        <TableBodyCellText variant={'dark'}>
                            {i18n.t('home:table:statuses:hard_cancelled')}
                        </TableBodyCellText>
                    </div>
                )
            case 'auto_cancelled':
                return (
                    <div className="flex items-center gap-2">
                        <StatusDot variant={'purple'} />{' '}
                        <TableBodyCellText variant={'dark'}>
                            {i18n.t('home:table:statuses:auto_cancelled')}
                        </TableBodyCellText>
                    </div>
                )
            case 'pending_payment':
                return (
                    <div className="flex items-center gap-2">
                        <StatusDot variant={'blue'} />{' '}
                        <TableBodyCellText variant={'dark'}>
                            {assignment.is_paid_via_upwork && assignment.task_media.name == 'walkthrough'
                                ? i18n.t('home:table:statuses:walkthrough_upwork')
                                : i18n.t('home:table:statuses:pending_payment')}
                        </TableBodyCellText>
                    </div>
                )
            case 'soft_cancelled':
                return (
                    <div className="flex items-center gap-2">
                        <StatusDot variant={'purple'} />{' '}
                        <TableBodyCellText variant={'dark'}>
                            {i18n.t('home:table:statuses:soft_cancelled')}
                        </TableBodyCellText>
                    </div>
                )
            case 'completed':
                return (
                    <div className="flex items-center gap-2">
                        <StatusDot variant={'success'} />{' '}
                        <TableBodyCellText variant={'dark'}>
                            {i18n.t('home:table:statuses:completed')}
                        </TableBodyCellText>
                    </div>
                )
        }

        if (assignment.task_status.post_prod_status == 'upload_completed') {
            return (
                <div className="flex items-center gap-2">
                    <StatusDot variant={'blue'} />{' '}
                    <TableBodyCellText variant={'dark'}>
                        {assignment.is_paid_via_upwork && assignment.task_media.name == 'walkthrough'
                            ? i18n.t('home:table:statuses:walkthrough_upwork')
                            : i18n.t('home:table:statuses:processing_payment')}
                    </TableBodyCellText>
                </div>
            )
        } else if (assignment.task_status.coordination_status == 'cancellation_requested') {
            return (
                <div className="flex items-center gap-2">
                    <StatusDot variant={'blue'} />{' '}
                    <TableBodyCellText variant={'dark'}>
                        {i18n.t('home:table:statuses:cancellation_progress')}
                    </TableBodyCellText>
                </div>
            )
        }
    }
    switch (assignment.declined_type) {
        case 'hard_declined':
            return (
                <div className="flex items-center gap-2">
                    <StatusDot variant={'neutral'} />{' '}
                    <TableBodyCellText variant={'dark'}>{i18n.t('home:table:statuses:declined')}</TableBodyCellText>
                </div>
            )
        case 'soft_declined':
            return (
                <div className="flex items-center gap-2">
                    <StatusDot variant={'neutral'} />{' '}
                    <TableBodyCellText variant={'dark'}>
                        {i18n.t('home:table:statuses:opportunity_expired')}
                    </TableBodyCellText>
                </div>
            )
    }

    switch (assignment.status) {
        case 'auto_cancelled':
            return (
                <div className="flex items-center gap-2">
                    <StatusDot variant={'purple'} />{' '}
                    <TableBodyCellText variant={'dark'}>
                        {i18n.t('home:table:statuses:auto_cancelled')}
                    </TableBodyCellText>
                </div>
            )
        case 'declined':
            return (
                <div className="flex items-center gap-2">
                    <StatusDot variant={'neutral'} />{' '}
                    <TableBodyCellText variant={'dark'}>{i18n.t('home:table:statuses:declined')}</TableBodyCellText>
                </div>
            )
        case 'soft_cancelled':
            return (
                <div className="flex items-center gap-2">
                    <StatusDot variant={'purple'} />{' '}
                    <TableBodyCellText variant={'dark'}>
                        {i18n.t('home:table:statuses:soft_cancelled')}
                    </TableBodyCellText>
                </div>
            )
        case 'hard_cancelled':
            return (
                <div className="flex items-center gap-2">
                    <StatusDot variant={'purple'} />{' '}
                    <TableBodyCellText variant={'dark'}>
                        {i18n.t('home:table:statuses:hard_cancelled')}
                    </TableBodyCellText>
                </div>
            )
        case 'pending':
            return (
                <div className="flex items-center gap-2">
                    <StatusDot variant={'blue'} />{' '}
                    <TableBodyCellText variant={'dark'}>{i18n.t('home:table:statuses:pending')}</TableBodyCellText>
                </div>
            )
    }
}
